import { useState, useEffect } from 'react';
import { Brain, FileText, Users, Sparkles, List } from 'lucide-react';
import { KeywordsTab } from './tabs/KeywordsTab';
import { RecommendationsTab } from './tabs/RecommendationsTab';
import { CompetitorsTab } from './tabs/CompetitorsTab';
import { AITab } from './tabs/AITab';
import { HeaderStructure } from './HeaderStructure';
import { supabaseClient } from '../../../../lib/supabase';

interface RightSidebarProps {
  activeTab: 'keywords' | 'recommendations' | 'competitors' | 'ai' | 'headers';
  onTabChange: (tab: 'keywords' | 'recommendations' | 'competitors' | 'ai' | 'headers') => void;
  analysisId?: string;
  updateTrigger?: number;
  quillInstance?: any;
}

export default function RightSidebar({ 
  activeTab, 
  onTabChange, 
  analysisId, 
  updateTrigger = 0,
  quillInstance
}: RightSidebarProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keywords, setKeywords] = useState<any[]>([]);
  const [additionalKeywords, setAdditionalKeywords] = useState<Set<string>>(new Set<string>());
  const [competitors, setCompetitors] = useState<any[]>([]);
  const [contentAnalysis, setContentAnalysis] = useState<any>(null);
  const [filteredMissingKeywords, setFilteredMissingKeywords] = useState<string[]>([]);
  const [filteredPresentKeywords, setFilteredPresentKeywords] = useState<string[]>([]);

  useEffect(() => {
    const loadAnalysis = async () => {
      if (!analysisId) return;

      try {
        setLoading(true);
        const { data: analysis, error: analysisError } = await supabaseClient
          .from('analyses')
          .select('*')
          .eq('id', analysisId)
          .single();

        if (analysisError) throw analysisError;

        if (analysis?.content_analysis) {
          setContentAnalysis(analysis.content_analysis);
          
          // Set competitors
          const selectedDomains = analysis.content_analysis.selectedDomains || [];
          const filteredCompetitors = analysis.content_analysis.competitors
            ?.filter((comp: any) => comp?.domain && selectedDomains.includes(comp.domain))
            .map((comp: any) => ({
              domain: comp.domain,
              url: comp.url,
              headers: comp.headers || {}
            })) || [];
          setCompetitors(filteredCompetitors);
        }

        // Load keyword analysis or generate from content_analysis if not exists
        if (analysis?.keyword_analysis?.keywords?.length) {
          // Update keyword status based on current editor content
          const editorContent = analysis.editor_content || '';
          const editorContentLower = editorContent.toLowerCase();
          
          const processedKeywords = analysis.keyword_analysis.keywords.map((k: any) => {
            const keywordExists = k.keyword && 
              editorContentLower.includes(k.keyword.toLowerCase());
            
            const correctedStatus = keywordExists ? 'Present' : 'Missing';
            
            return {
              ...k,
              status: correctedStatus,
              density: keywordExists ? (k.density || 0) : 0,
              densityRatio: keywordExists ? (k.densityRatio || 0) : 0
            };
          });

          setKeywords(processedKeywords);
        } else if (analysis?.content_analysis?.keywordAnalysis) {
          // If keyword_analysis is not set, generate it from content_analysis
          console.log('Generating keyword analysis from content_analysis');
          const editorContent = analysis.editor_content || '';
          const editorContentLower = editorContent.toLowerCase();
          
          const keywordAnalysisData = analysis.content_analysis.keywordAnalysis;
          const allKeywords = new Set<string>();
          
          // Collect all unique keywords from competitors
          Object.values(keywordAnalysisData).forEach((competitorData: any) => {
            if (competitorData?.keywords) {
              Object.keys(competitorData.keywords).forEach(keyword => {
                allKeywords.add(keyword.toLowerCase());
              });
            }
          });
          
          // Generate keyword metrics
          const processedKeywords = Array.from(allKeywords).map(keyword => {
            const keywordExists = editorContentLower.includes(keyword);
            
            // Calculate average density and presence from competitors
            let totalDensity = 0;
            let presenceCount = 0;
            let competitorsCount = 0;
            
            Object.values(keywordAnalysisData).forEach((competitorData: any) => {
              if (competitorData?.keywords?.[keyword]) {
                const kwData = competitorData.keywords[keyword];
                totalDensity += kwData.density || 0;
                presenceCount++;
              }
              competitorsCount++;
            });
            
            const avgDensity = presenceCount > 0 ? totalDensity / presenceCount : 0;
            const presencePercentage = competitorsCount > 0 ? (presenceCount / competitorsCount) * 100 : 0;
            
            return {
              keyword,
              status: keywordExists ? 'Present' : 'Missing',
              density: 0,
              avgDensity,
              densityRatio: 0,
              presencePercentage,
              competitorsCount: presenceCount,
              totalCompetitors: competitorsCount,
              importance: presencePercentage >= 50 ? 10 : 5
            };
          });
          
          setKeywords(processedKeywords);
        }

        if (analysis?.additional_keywords) {
          // Convert array to Set for faster lookups
          setAdditionalKeywords(new Set<string>(analysis.additional_keywords.map((k: string) => k.toLowerCase())));
        }

        setError(null);
      } catch (err) {
        console.error('Error loading analysis:', err);
        setError(err instanceof Error ? err.message : 'Failed to load analysis');
      } finally {
        setLoading(false);
      }
    };

    loadAnalysis();
  }, [analysisId, updateTrigger]);

  const handleFilteredKeywordsChange = (missing: string[], present: string[]) => {
    setFilteredMissingKeywords(missing);
    setFilteredPresentKeywords(present);
  };

  return (
    <div className="w-[420px] bg-white border-l border-gray-200 flex flex-col">
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px">
          <button
            onClick={() => onTabChange('keywords')}
            className={`flex-1 px-2 py-2 text-xs font-medium text-center ${
              activeTab === 'keywords'
                ? 'border-b-2 border-indigo-500 text-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Brain className="h-4 w-4 mx-auto mb-1" />
            Keywords
          </button>
          <button
            onClick={() => onTabChange('recommendations')}
            className={`flex-1 px-2 py-2 text-xs font-medium text-center ${
              activeTab === 'recommendations'
                ? 'border-b-2 border-indigo-500 text-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileText className="h-4 w-4 mx-auto mb-1" />
            Recs
          </button>
          <button
            onClick={() => onTabChange('competitors')}
            className={`flex-1 px-2 py-2 text-xs font-medium text-center ${
              activeTab === 'competitors'
                ? 'border-b-2 border-indigo-500 text-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Users className="h-4 w-4 mx-auto mb-1" />
            Comps
          </button>
          <button
            onClick={() => onTabChange('ai')}
            className={`flex-1 px-2 py-2 text-xs font-medium text-center ${
              activeTab === 'ai'
                ? 'border-b-2 border-indigo-500 text-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Sparkles className="h-4 w-4 mx-auto mb-1" />
            AI
          </button>
          <button
            onClick={() => onTabChange('headers')}
            className={`flex-1 px-2 py-2 text-xs font-medium text-center ${
              activeTab === 'headers'
                ? 'border-b-2 border-indigo-500 text-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <List className="h-4 w-4 mx-auto mb-1" />
            Headers
          </button>
        </nav>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {activeTab === 'keywords' && (
          <KeywordsTab
            keywords={keywords}
            additionalKeywords={additionalKeywords}
            loading={loading}
            error={error}
            onFilteredKeywordsChange={handleFilteredKeywordsChange}
          />
        )}
        {activeTab === 'recommendations' && (
          <RecommendationsTab
            keywords={keywords}
            pageKeywords={Array.from(additionalKeywords).map(keyword => ({
              keyword,
              importance: 10,
              status: 'Page',
              density: 0,
              avgDensity: 0,
              densityRatio: 0,
              competitorsCount: 0,
              totalCompetitors: 0,
              isAdditional: true
            }))}
            loading={loading}
            error={error}
          />
        )}
        {activeTab === 'competitors' && (
          <CompetitorsTab
            competitors={competitors}
          />
        )}
        {activeTab === 'ai' && (
          <AITab
            analysisId={analysisId || ''}
            filteredMissingKeywords={filteredMissingKeywords}
            filteredPresentKeywords={filteredPresentKeywords}
          />
        )}
        {activeTab === 'headers' && (
          <HeaderStructure
            quillInstance={quillInstance}
          />
        )}
      </div>
    </div>
  );
}